import { z } from 'zod';

// =============================================================================
// CORE DATA MODELS
// =============================================================================

// Document Category Enum
export const DocumentCategorySchema = z.enum([
  'Statute',
  'Regulation', 
  'Technical Standard',
  'News',
  'User Document'
]);
export type DocumentCategory = z.infer<typeof DocumentCategorySchema>;

// Document Type Enum
export const DocumentTypeSchema = z.enum(['public', 'private']);
export type DocumentType = z.infer<typeof DocumentTypeSchema>;

// Document Metadata Schema
export const DocumentMetadataSchema = z.object({
  pageCount: z.number().optional(),
  fileSize: z.number().optional(),
  mimeType: z.string().optional(),
  extractionMethod: z.enum(['tika', 'ocr']).optional(),
  ocrConfidence: z.number().min(0).max(1).optional()
});
export type DocumentMetadata = z.infer<typeof DocumentMetadataSchema>;

// Document Schema
export const DocumentSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1),
  sourceUrl: z.string().url().optional(),
  category: DocumentCategorySchema,
  documentType: DocumentTypeSchema,
  userId: z.string().uuid().optional(),
  contentHash: z.string().min(1),
  ingestedAt: z.date(),
  metadata: DocumentMetadataSchema,
  filePath: z.string().optional()
});
export type Document = z.infer<typeof DocumentSchema>;

// Document Chunk Schema
export const DocumentChunkSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  chunkIndex: z.number().min(0),
  content: z.string().min(1),
  embedding: z.array(z.number()).length(1536), // OpenAI text-embedding-3-small
  pageNumber: z.number().min(1).optional(),
  tokenCount: z.number().min(1).optional()
});
export type DocumentChunk = z.infer<typeof DocumentChunkSchema>;

// Citation Schema
export const CitationSchema = z.object({
  documentId: z.string().uuid(),
  documentTitle: z.string().min(1),
  pageNumber: z.number().min(1).optional(),
  sourceUrl: z.string().url().optional(),
  chunkId: z.string().uuid(),
  relevanceScore: z.number().min(0).max(1)
});
export type Citation = z.infer<typeof CitationSchema>;

// Chat Message Schema
export const ChatMessageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1),
  citations: z.array(CitationSchema).default([]),
  tokensUsed: z.number().min(0).optional(),
  createdAt: z.date()
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

// Conversation Schema
export const ConversationSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  title: z.string().optional(),
  tags: z.array(z.string()).default([]),
  isFavorite: z.boolean().default(false),
  sharedWith: z.array(z.string().uuid()).default([]),
  exportSettings: z.record(z.unknown()).default({}),
  retentionPolicy: z.record(z.unknown()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  messages: z.array(ChatMessageSchema).default([])
});
export type Conversation = z.infer<typeof ConversationSchema>;

// User Schema
export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  createdAt: z.date(),
  updatedAt: z.date()
});
export type User = z.infer<typeof UserSchema>;

// Usage Event Schema
export const UsageEventSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  eventType: z.enum(['chat_query', 'document_upload']),
  tokensUsed: z.number().min(0).default(0),
  metadata: z.record(z.unknown()).default({}),
  createdAt: z.date()
});
export type UsageEvent = z.infer<typeof UsageEventSchema>;

// User Subscription Schema
export const UserSubscriptionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  stripeSubscriptionId: z.string().optional(),
  planType: z.enum(['trial', 'pro', 'team', 'enterprise']),
  status: z.enum(['active', 'canceled', 'past_due']),
  currentPeriodStart: z.date().optional(),
  currentPeriodEnd: z.date().optional(),
  queryLimit: z.number().min(0),
  queryCount: z.number().min(0).default(0)
});
export type UserSubscription = z.infer<typeof UserSubscriptionSchema>;

// =============================================================================
// DOCUMENT PROCESSING TYPES
// =============================================================================

// Crawling configuration
export const CrawlConfigSchema = z.object({
  baseUrl: z.string().url(),
  maxDepth: z.number().min(1).max(10),
  allowedDomains: z.array(z.string()),
  excludePatterns: z.array(z.string()),
  rateLimit: z.number().min(0.1).max(10),
  userAgent: z.string().min(1),
});
export type CrawlConfig = z.infer<typeof CrawlConfigSchema>;

// Chunking configuration
export const ChunkingConfigSchema = z.object({
  maxTokens: z.number().min(50).max(2000),
  overlapTokens: z.number().min(0).max(500),
  minChunkSize: z.number().min(10).max(1000),
  preserveSentences: z.boolean().default(true),
});
export type ChunkingConfig = z.infer<typeof ChunkingConfigSchema>;

// OpenAI configuration
export const EmbeddingConfigSchema = z.object({
  model: z.string().default('text-embedding-3-small'),
  dimensions: z.number().default(1536),
  batchSize: z.number().min(1).max(100).default(10),
});
export type EmbeddingConfig = z.infer<typeof EmbeddingConfigSchema>;

// Processed Document Schema
export const ProcessedDocumentSchema = z.object({
  id: z.string().uuid(),
  metadata: DocumentMetadataSchema,
  chunks: z.array(DocumentChunkSchema),
  processingStatus: z.enum(['pending', 'processing', 'completed', 'failed']),
  error: z.string().optional(),
});
export type ProcessedDocument = z.infer<typeof ProcessedDocumentSchema>;

// =============================================================================
// FILTER AND SEARCH TYPES
// =============================================================================

// Date Range Schema
export const DateRangeSchema = z.object({
  start: z.date(),
  end: z.date()
}).refine(data => data.start <= data.end, {
  message: "Start date must be before or equal to end date"
});
export type DateRange = z.infer<typeof DateRangeSchema>;

// Document Filters Schema
export const DocumentFiltersSchema = z.object({
  categories: z.array(z.string()).default([]),
  dateRange: DateRangeSchema.optional(),
  documentSource: z.enum(['public', 'private', 'all']).default('all')
});
export type DocumentFilters = z.infer<typeof DocumentFiltersSchema>;

// Search Scope Schema
export const SearchScopeSchema = z.enum(['public', 'private', 'all']);
export type SearchScope = z.infer<typeof SearchScopeSchema>;

// =============================================================================
// ERROR HANDLING TYPES
// =============================================================================

// API Error Schema
export const APIErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
  timestamp: z.date()
});
export type APIError = z.infer<typeof APIErrorSchema>;
// =============================================================================
// API REQUEST/RESPONSE INTERFACES
// =============================================================================

// Chat API Types
export const ChatRequestSchema = z.object({
  message: z.string().min(1).max(4000),
  conversationId: z.string().uuid().optional(),
  filters: DocumentFiltersSchema.optional(),
  searchScope: SearchScopeSchema.default('all')
});
export type ChatRequest = z.infer<typeof ChatRequestSchema>;

export const ChatResponseSchema = z.object({
  response: z.string().min(1),
  citations: z.array(CitationSchema).default([]),
  conversationId: z.string().uuid(),
  tokensUsed: z.number().min(0)
});
export type ChatResponse = z.infer<typeof ChatResponseSchema>;

// Document Upload API Types
export const UploadRequestSchema = z.object({
  fileName: z.string().min(1),
  fileSize: z.number().min(1),
  mimeType: z.string().min(1),
  metadata: DocumentMetadataSchema.optional()
});
export type UploadRequest = z.infer<typeof UploadRequestSchema>;

export const UploadResultSchema = z.object({
  documentId: z.string().uuid(),
  status: z.enum(['processing', 'completed', 'failed']),
  extractedText: z.string().optional(),
  error: z.string().optional()
});
export type UploadResult = z.infer<typeof UploadResultSchema>;

export const ProcessingStatusSchema = z.object({
  documentId: z.string().uuid(),
  status: z.enum(['pending', 'processing', 'completed', 'failed']),
  progress: z.number().min(0).max(100).default(0),
  error: z.string().optional(),
  updatedAt: z.date()
});
export type ProcessingStatus = z.infer<typeof ProcessingStatusSchema>;

// Document Management API Types
export const UserDocumentSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1),
  category: DocumentCategorySchema,
  fileSize: z.number().min(0),
  mimeType: z.string(),
  uploadedAt: z.date(),
  status: z.enum(['processing', 'completed', 'failed']),
  extractedText: z.string().optional()
});
export type UserDocument = z.infer<typeof UserDocumentSchema>;

export const DocumentListResponseSchema = z.object({
  documents: z.array(UserDocumentSchema),
  totalCount: z.number().min(0),
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20)
});
export type DocumentListResponse = z.infer<typeof DocumentListResponseSchema>;

export const DeleteDocumentRequestSchema = z.object({
  documentId: z.string().uuid()
});
export type DeleteDocumentRequest = z.infer<typeof DeleteDocumentRequestSchema>;

// Conversation Management API Types
export const ConversationListResponseSchema = z.object({
  conversations: z.array(ConversationSchema.omit({ messages: true }).extend({
    messageCount: z.number().min(0),
    lastMessageAt: z.date(),
    relevanceScore: z.number().min(0).optional()
  })),
  totalCount: z.number().min(0),
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20)
});
export type ConversationListResponse = z.infer<typeof ConversationListResponseSchema>;

export const ConversationSearchRequestSchema = z.object({
  query: z.string().min(1).max(200).optional(),
  tags: z.array(z.string()).optional(),
  favoritesOnly: z.boolean().default(false),
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20)
});
export type ConversationSearchRequest = z.infer<typeof ConversationSearchRequestSchema>;

export const ConversationUpdateRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  tags: z.array(z.string().min(1).max(50)).max(10).optional(),
  isFavorite: z.boolean().optional()
});
export type ConversationUpdateRequest = z.infer<typeof ConversationUpdateRequestSchema>;

export const ConversationExportRequestSchema = z.object({
  conversationId: z.string().uuid(),
  format: z.enum(['json', 'pdf', 'txt', 'csv']),
  includeMetadata: z.boolean().default(true),
  includeCitations: z.boolean().default(true)
});
export type ConversationExportRequest = z.infer<typeof ConversationExportRequestSchema>;

export const ConversationExportResponseSchema = z.object({
  exportId: z.string().uuid(),
  status: z.enum(['pending', 'processing', 'completed', 'failed']),
  downloadUrl: z.string().url().optional(),
  error: z.string().optional(),
  createdAt: z.date()
});
export type ConversationExportResponse = z.infer<typeof ConversationExportResponseSchema>;

export const ConversationShareRequestSchema = z.object({
  conversationId: z.string().uuid(),
  shareWithEmail: z.string().email(),
  permissionLevel: z.enum(['read', 'comment', 'edit']).default('read'),
  expiresInDays: z.number().min(1).max(365).optional()
});
export type ConversationShareRequest = z.infer<typeof ConversationShareRequestSchema>;

export const ConversationShareResponseSchema = z.object({
  shareId: z.string().uuid(),
  sharedWith: z.string().email(),
  permissionLevel: z.enum(['read', 'comment', 'edit']),
  expiresAt: z.date().optional(),
  createdAt: z.date()
});
export type ConversationShareResponse = z.infer<typeof ConversationShareResponseSchema>;

export const ConversationDeleteRequestSchema = z.object({
  conversationId: z.string().uuid(),
  permanent: z.boolean().default(false)
});
export type ConversationDeleteRequest = z.infer<typeof ConversationDeleteRequestSchema>;

// Usage Tracking API Types
export const UsageTrackingRequestSchema = z.object({
  eventType: z.enum(['chat_query', 'document_upload']),
  tokensUsed: z.number().min(0).default(0),
  metadata: z.record(z.unknown()).default({})
});
export type UsageTrackingRequest = z.infer<typeof UsageTrackingRequestSchema>;

export const UsageStatsResponseSchema = z.object({
  currentPeriodUsage: z.number().min(0),
  currentPeriodLimit: z.number().min(0),
  usagePercentage: z.number().min(0).max(100),
  planType: z.enum(['trial', 'pro', 'team', 'enterprise']),
  periodStart: z.date(),
  periodEnd: z.date()
});
export type UsageStatsResponse = z.infer<typeof UsageStatsResponseSchema>;

// Billing and Subscription API Types
export const PlanDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  price: z.number().min(0),
  currency: z.string().default('usd'),
  interval: z.enum(['month', 'year']),
  queryLimit: z.number().min(0),
  features: z.array(z.string()),
  stripePriceId: z.string()
});
export type PlanDetails = z.infer<typeof PlanDetailsSchema>;

export const SubscriptionDetailsSchema = z.object({
  id: z.string(),
  userId: z.string(),
  stripeSubscriptionId: z.string().optional(),
  stripeCustomerId: z.string().optional(),
  planType: z.enum(['trial', 'pro', 'team', 'enterprise']),
  status: z.enum(['active', 'canceled', 'past_due', 'trialing', 'incomplete']),
  currentPeriodStart: z.date().optional(),
  currentPeriodEnd: z.date().optional(),
  cancelAtPeriodEnd: z.boolean().default(false),
  queryLimit: z.number().min(0),
  queryCount: z.number().min(0).default(0),
  createdAt: z.date(),
  updatedAt: z.date()
});
export type SubscriptionDetails = z.infer<typeof SubscriptionDetailsSchema>;

export const CreateCheckoutSessionRequestSchema = z.object({
  priceId: z.string(),
  successUrl: z.string().url(),
  cancelUrl: z.string().url()
});
export type CreateCheckoutSessionRequest = z.infer<typeof CreateCheckoutSessionRequestSchema>;

export const CreateCheckoutSessionResponseSchema = z.object({
  sessionId: z.string(),
  url: z.string().url()
});
export type CreateCheckoutSessionResponse = z.infer<typeof CreateCheckoutSessionResponseSchema>;

export const CreatePortalSessionRequestSchema = z.object({
  returnUrl: z.string().url()
});
export type CreatePortalSessionRequest = z.infer<typeof CreatePortalSessionRequestSchema>;

export const CreatePortalSessionResponseSchema = z.object({
  url: z.string().url()
});
export type CreatePortalSessionResponse = z.infer<typeof CreatePortalSessionResponseSchema>;

export const StripeWebhookEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  data: z.object({
    object: z.record(z.unknown())
  }),
  created: z.number()
});
export type StripeWebhookEvent = z.infer<typeof StripeWebhookEventSchema>;

// Authentication API Types
export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8)
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const SignupRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
  confirmPassword: z.string()
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});
export type SignupRequest = z.infer<typeof SignupRequestSchema>;

export const AuthResponseSchema = z.object({
  user: UserSchema,
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresAt: z.date()
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

// Generic API Response Wrapper
export const APIResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) => z.object({
  success: z.boolean(),
  data: dataSchema.optional(),
  error: APIErrorSchema.optional(),
  timestamp: z.date()
});

export type APIResponse<T> = {
  success: boolean;
  data?: T;
  error?: APIError;
  timestamp: Date;
};

// Pagination Types
export const PaginationRequestSchema = z.object({
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc')
});
export type PaginationRequest = z.infer<typeof PaginationRequestSchema>;

export const PaginationResponseSchema = z.object({
  page: z.number().min(1),
  pageSize: z.number().min(1),
  totalCount: z.number().min(0),
  totalPages: z.number().min(0),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean()
});
export type PaginationResponse = z.infer<typeof PaginationResponseSchema>;

// Export admin types
export * from './admin';
export * from './notifications';
export * from './errors';